import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import getUserInfo from "../utilities/decodeJwt";

const navLinkClass = ({ isActive }) =>
  [
    "rounded-lg px-3 py-2 text-sm font-medium transition",
    isActive
      ? "bg-cinema-800 text-cinema-accent"
      : "text-slate-400 hover:bg-cinema-800/70 hover:text-slate-100",
  ].join(" ");

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(() => getUserInfo());
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const displayName = user?.username || user?.id || "";

  useEffect(() => {
    setUser(getUserInfo());
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (e) => {
      if (!menuRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    setUser(undefined);
    setMenuOpen(false);
    navigate("/", { replace: true });
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-cinema-950/90 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link
          to="/game"
          className="font-display text-lg font-semibold tracking-tight text-slate-100"
        >
          Movie<span className="text-cinema-accent">Duel</span>
        </Link>
        <nav className="flex flex-wrap items-center gap-1">
          <NavLink to="/" className={navLinkClass} end>
            Home
          </NavLink>
          <NavLink to="/leaderboards" className={navLinkClass}>
            Leaderboards
          </NavLink>
        </nav>

        <div
          ref={menuRef}
          className="relative min-w-0 shrink-0"
        >
          {displayName ? (
            <>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
                className={`flex max-w-[160px] items-center gap-1 truncate rounded-lg px-2 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-cinema-800/80 hover:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cinema-accent/50 ${
                  menuOpen ? "bg-cinema-800 text-slate-100" : ""
                }`}
              >
                <span className="truncate font-mono">{displayName}</span>
                <span className="text-slate-500" aria-hidden>
                  ▾
                </span>
              </button>
              {menuOpen ? (
                <div
                  className="absolute right-0 z-50 mt-1 min-w-[10rem] rounded-xl border border-slate-700/80 bg-cinema-900 py-1 shadow-panel"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-200 transition hover:bg-cinema-800 hover:text-white"
                  >
                    Log out
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <Link
              to="/login"
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-cinema-accent hover:text-cinema-accent-hover"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
